import { useState, useCallback, DragEvent } from "react";

interface UseDragAndDropOptions {
  onFileDrop: (files: FileList) => void;
  acceptedFileTypes?: string[];
  maxFileSize?: number; // in bytes
  multiple?: boolean;
}

interface UseDragAndDropReturn {
  isDragOver: boolean;
  isDragActive: boolean;
  dragHandlers: {
    onDragEnter: (e: DragEvent<HTMLElement>) => void;
    onDragLeave: (e: DragEvent<HTMLElement>) => void;
    onDragOver: (e: DragEvent<HTMLElement>) => void;
    onDrop: (e: DragEvent<HTMLElement>) => void;
  };
  validateFiles: (files: FileList) => { validFiles: File[]; errors: string[] };
}

export const useDragAndDrop = ({
  onFileDrop,
  acceptedFileTypes,
  maxFileSize,
  multiple = false,
}: UseDragAndDropOptions): UseDragAndDropReturn => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);

  const validateFiles = useCallback(
    (files: FileList): { validFiles: File[]; errors: string[] } => {
      const validFiles: File[] = [];
      const errors: string[] = [];

      Array.from(files).forEach((file) => {
        // Check file type
        if (acceptedFileTypes && acceptedFileTypes.length > 0) {
          const isValidType = acceptedFileTypes.some((type) => {
            if (type.startsWith(".")) {
              return file.name.toLowerCase().endsWith(type.toLowerCase());
            }
            return file.type.match(type);
          });

          if (!isValidType) {
            errors.push(`${file.name}: Type de fichier non supporté`);
            return;
          }
        }

        // Check file size
        if (maxFileSize && file.size > maxFileSize) {
          const maxSizeMB = (maxFileSize / (1024 * 1024)).toFixed(1);
          errors.push(
            `${file.name}: Fichier trop volumineux (max ${maxSizeMB}MB)`
          );
          return;
        }

        validFiles.push(file);
      });

      // Check multiple files
      if (!multiple && validFiles.length > 1) {
        errors.push("Un seul fichier autorisé");
        return { validFiles: validFiles.slice(0, 1), errors };
      }

      return { validFiles, errors };
    },
    [acceptedFileTypes, maxFileSize, multiple]
  );

  const handleDragEnter = useCallback((e: DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();

    // Only set drag active to false if we're leaving the drop zone entirely
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;

    if (x < rect.left || x >= rect.right || y < rect.top || y >= rect.bottom) {
      setIsDragActive(false);
      setIsDragOver(false);
    }
  }, []);

  const handleDragOver = useCallback((e: DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDrop = useCallback(
    (e: DragEvent<HTMLElement>) => {
      e.preventDefault();
      e.stopPropagation();

      setIsDragActive(false);
      setIsDragOver(false);

      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        onFileDrop(files);
      }
    },
    [onFileDrop]
  );

  return {
    isDragOver,
    isDragActive,
    dragHandlers: {
      onDragEnter: handleDragEnter,
      onDragLeave: handleDragLeave,
      onDragOver: handleDragOver,
      onDrop: handleDrop,
    },
    validateFiles,
  };
};
